/**
 * date-helpers — small date utility set for the timesheet view.
 *
 * Constraint: no runtime deps (date-fns / luxon are off the table per the
 * Phase 3c task scope). We lean on `Intl` for human-readable formatting
 * and hand-roll the few non-trivial pieces (ISO-week key, range presets,
 * day boundaries in local TZ).
 *
 * Every function accepts/returns ISO-date strings (`YYYY-MM-DD`) for
 * range bounds and `Date` objects internally. Times are stored in UTC by
 * the BE (ISO timestamps); display-side formatting always reads through
 * the user's locale + timezone.
 */

const DAY_MS = 86_400_000

/** Pad a non-negative integer to 2 digits. */
function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** Convert a `Date` to a local `YYYY-MM-DD` string (no TZ conversion). */
export function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

/** Parse `YYYY-MM-DD` (or anything `new Date(...)` accepts) into a local-midnight `Date`. */
export function parseIsoDate(iso: string): Date {
  // Anchor to noon to dodge DST off-by-one when the cell is later
  // formatted via Intl in the user's locale.
  const [y, m, d] = iso.split('-').map((s) => Number.parseInt(s, 10))
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12, 0, 0, 0)
}

/** Today's local-midnight `Date`. */
export function startOfToday(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

/**
 * Add (or subtract) whole CALENDAR days, preserving the time-of-day.
 *
 * Deliberately `setDate()` rather than `+ days * DAY_MS`: a DST day is 23
 * or 25 hours long, so fixed-millisecond arithmetic drifts by an hour
 * across every transition and the drift is enough to change the DATE.
 * Measured in `America/New_York` before the fix: the week of the 2026
 * spring-forward resolved to `2026-03-07 .. 2026-03-14`, i.e. an
 * EIGHT-day week — `startOfWeek` retreated two days from local midnight
 * and landed at 23:00 the evening before, then the six-day step crossed
 * forward through the transition and gained the hour back. Every caller
 * here (`startOfWeek`, `endOfMonth`, the timesheet's `dayEnd`, the week
 * grid's seven columns) means calendar days, so calendar days is what
 * this returns.
 */
export function addDays(date: Date, days: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

/**
 * The user's preferred "first day of the week". Drives which day
 * appears in the leftmost column of every week-based view AND where
 * `startOfWeek` anchors. Mirrors the BE `users.first_day_of_week`
 * enum. Defaults to `'monday'` (ISO 8601) when the user hasn't picked.
 */
export type FirstDayOfWeek = 'monday' | 'sunday' | 'saturday'

const FIRST_DAY_OF_WEEK_INDEX: Record<FirstDayOfWeek, number> = {
  // `Date#getDay()`: 0=Sun, 1=Mon, ..., 6=Sat.
  monday: 1,
  sunday: 0,
  saturday: 6,
}

/**
 * Map a `FirstDayOfWeek` slug to the `getDay()` index it corresponds
 * to. Exported because some surfaces want the index directly (column
 * ordering, grid layout) rather than calling `startOfWeek`.
 */
export function firstDayToIndex(firstDay: FirstDayOfWeek): number {
  return FIRST_DAY_OF_WEEK_INDEX[firstDay]
}

/**
 * Week start anchored on the user's preferred first day. Defaults to
 * `'monday'` (ISO 8601) when no preference is supplied, matching the
 * pre-MOTE-FDOW behavior — every existing caller keeps working without
 * threading the pref. Callers that DO have the pref in scope (the
 * timesheet + master surfaces, via `useMe()`) pass it through so the
 * week boundary respects the user's choice.
 */
export function startOfWeek(date: Date, firstDay: FirstDayOfWeek = 'monday'): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  const firstIdx = firstDayToIndex(firstDay)
  // Days from the configured first-day. e.g. for Monday-start with
  // d.getDay()===3 (Wed): (3 - 1 + 7) % 7 = 2 → retreat 2 days to Mon.
  const dayOfWeek = d.getDay()
  const daysSinceStart = (dayOfWeek - firstIdx + 7) % 7
  return addDays(d, -daysSinceStart)
}

export function endOfWeek(date: Date, firstDay: FirstDayOfWeek = 'monday'): Date {
  return addDays(startOfWeek(date, firstDay), 6)
}

export function startOfMonth(date: Date): Date {
  const d = new Date(date)
  d.setDate(1)
  d.setHours(0, 0, 0, 0)
  return d
}

export function endOfMonth(date: Date): Date {
  const d = startOfMonth(date)
  d.setMonth(d.getMonth() + 1)
  return addDays(d, -1)
}

/**
 * Add (or subtract) whole calendar months, CLAMPING the day-of-month so
 * the result never rolls into the next month. `new Date(2026, 0, 31)`
 * with `setMonth(1)` produces March 3rd, not February 28th — which is
 * exactly the silent off-by-a-month that makes a "previous month" arrow
 * skip a month. Jan 31 + 1 → Feb 28 (or Feb 29 in a leap year).
 */
export function addMonths(date: Date, months: number): Date {
  const d = new Date(date)
  const day = d.getDate()
  d.setDate(1)
  d.setMonth(d.getMonth() + months)
  // Last day of the landed month: day 0 of the NEXT month.
  const lastDayOfTargetMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  d.setDate(Math.min(day, lastDayOfTargetMonth))
  d.setHours(0, 0, 0, 0)
  return d
}

export function startOfQuarter(date: Date): Date {
  const d = new Date(date)
  d.setMonth(Math.floor(d.getMonth() / 3) * 3, 1)
  d.setHours(0, 0, 0, 0)
  return d
}

export function endOfQuarter(date: Date): Date {
  return endOfMonth(addMonths(startOfQuarter(date), 2))
}

export function startOfYear(date: Date): Date {
  const d = new Date(date)
  d.setMonth(0, 1)
  d.setHours(0, 0, 0, 0)
  return d
}

export function endOfYear(date: Date): Date {
  const d = new Date(date)
  d.setMonth(11, 31)
  d.setHours(0, 0, 0, 0)
  return d
}

/* -------------------------------------------------------------------------- */
/*                              Range presets                                 */
/* -------------------------------------------------------------------------- */

/**
 * Named window shapes. The first four are the timesheet's original set;
 * the rest were added for the reports dashboard, whose period picker
 * reaches further back. The slug IS the URL value and the i18n key
 * (`t('presets.this-quarter')`), so renaming one is a URL break.
 */
export type DateRangePreset =
  | 'today'
  | 'this-week'
  | 'last-week'
  | 'this-month'
  | 'last-month'
  | 'this-quarter'
  | 'ytd'
  | 'last-12-months'

export interface DateRange {
  from: string
  to: string
}

export interface RangePresetOptions {
  /** Week anchor for the week-shaped presets. Defaults to `'monday'`. */
  firstDay?: FirstDayOfWeek
  /**
   * "Now" for the computation. Injectable so the preset tests can pin a
   * mid-month / Jan 2 / Dec 31 date instead of depending on the clock —
   * every one of these presets has an edge case that only shows up on a
   * specific calendar day.
   */
  today?: Date
}

export function rangeForPreset(
  preset: DateRangePreset,
  options: RangePresetOptions = {},
): DateRange {
  const { firstDay = 'monday' } = options
  const today = options.today ? startOfDay(options.today) : startOfToday()
  switch (preset) {
    case 'today':
      return { from: toIsoDate(today), to: toIsoDate(today) }
    case 'this-week': {
      const start = startOfWeek(today, firstDay)
      return { from: toIsoDate(start), to: toIsoDate(addDays(start, 6)) }
    }
    case 'last-week': {
      const start = addDays(startOfWeek(today, firstDay), -7)
      return { from: toIsoDate(start), to: toIsoDate(addDays(start, 6)) }
    }
    case 'this-month':
      return { from: toIsoDate(startOfMonth(today)), to: toIsoDate(endOfMonth(today)) }
    case 'last-month': {
      const start = addMonths(startOfMonth(today), -1)
      return { from: toIsoDate(start), to: toIsoDate(endOfMonth(start)) }
    }
    case 'this-quarter':
      return { from: toIsoDate(startOfQuarter(today)), to: toIsoDate(endOfQuarter(today)) }
    case 'ytd':
      // Year-to-DATE: the window ends today, not at the end of December.
      return { from: toIsoDate(startOfYear(today)), to: toIsoDate(today) }
    case 'last-12-months': {
      // Twelve WHOLE calendar months ending with the current one, so the
      // month buckets the BE returns line up with the label ("Oct 2025 –
      // Sep 2026") instead of starting mid-month.
      const start = addMonths(startOfMonth(today), -11)
      return { from: toIsoDate(start), to: toIsoDate(endOfMonth(today)) }
    }
    default: {
      const _exhaustive: never = preset
      return _exhaustive
    }
  }
}

/** Local-midnight copy of a date, without mutating the input. */
function startOfDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

/**
 * English labels for the timesheet's own preset chips. NOT the reports
 * surface — that renders `t('presets.<slug>')` from the `reports`
 * catalog. Kept total so widening `DateRangePreset` can't leave a hole,
 * but the reports presets are never shown through this map.
 */
export const PRESET_LABELS: Record<DateRangePreset, string> = {
  today: 'Today',
  'this-week': 'This week',
  'last-week': 'Last week',
  'this-month': 'This month',
  'last-month': 'Last month',
  'this-quarter': 'This quarter',
  ytd: 'Year to date',
  'last-12-months': 'Last 12 months',
}

/* -------------------------------------------------------------------------- */
/*                              Range shifting                                */
/* -------------------------------------------------------------------------- */

/**
 * The step a `‹` / `›` press takes. Calendar units (`month`, `quarter`,
 * `year`, `months12`) RE-DERIVE the end of the unit rather than adding a
 * fixed day count, so a 31-day month never bleeds into the next one;
 * `days` is the generic fallback for a hand-picked custom window and
 * shifts by exactly its own length.
 */
export type RangeShiftUnit = 'day' | 'days' | 'week' | 'month' | 'quarter' | 'year' | 'months12'

/** Inclusive day count of a range (`from === to` ⇒ 1). */
export function daysInRange(range: DateRange): number {
  const from = parseIsoDate(range.from)
  const to = parseIsoDate(range.to)
  return Math.round((to.getTime() - from.getTime()) / DAY_MS) + 1
}

/** Which unit the `‹` / `›` arrows step by for a given preset. */
export function shiftUnitForPreset(preset: DateRangePreset): RangeShiftUnit {
  switch (preset) {
    case 'today':
      return 'day'
    case 'this-week':
    case 'last-week':
      return 'week'
    case 'this-month':
    case 'last-month':
      return 'month'
    case 'this-quarter':
      return 'quarter'
    case 'ytd':
      return 'year'
    case 'last-12-months':
      return 'months12'
    default: {
      const _exhaustive: never = preset
      return _exhaustive
    }
  }
}

/** Step a window forward (`delta > 0`) or back (`delta < 0`) by one unit. */
export function shiftRange(range: DateRange, delta: number, unit: RangeShiftUnit): DateRange {
  const from = parseIsoDate(range.from)
  const to = parseIsoDate(range.to)

  switch (unit) {
    case 'day':
      return { from: toIsoDate(addDays(from, delta)), to: toIsoDate(addDays(to, delta)) }
    case 'days': {
      // A DST boundary inside the window would make an hour-based length
      // wrong; `daysInRange` counts noon-anchored whole days, so a week
      // stays seven days across the spring-forward Sunday.
      const span = daysInRange(range)
      const step = delta * span
      return { from: toIsoDate(addDays(from, step)), to: toIsoDate(addDays(to, step)) }
    }
    case 'week':
      return { from: toIsoDate(addDays(from, delta * 7)), to: toIsoDate(addDays(to, delta * 7)) }
    case 'month': {
      const nextFrom = addMonths(startOfMonth(from), delta)
      return { from: toIsoDate(nextFrom), to: toIsoDate(endOfMonth(nextFrom)) }
    }
    case 'quarter': {
      const nextFrom = startOfQuarter(addMonths(startOfQuarter(from), delta * 3))
      return { from: toIsoDate(nextFrom), to: toIsoDate(endOfQuarter(nextFrom)) }
    }
    case 'year': {
      // Year-to-date shifts to the SAME day of the neighbouring year and
      // re-anchors `from` to that year's January 1st — a plain ±365 days
      // would drift by a day on every leap year.
      const nextTo = addMonths(to, delta * 12)
      return { from: toIsoDate(startOfYear(nextTo)), to: toIsoDate(nextTo) }
    }
    case 'months12': {
      const nextFrom = addMonths(startOfMonth(from), delta * 12)
      return { from: toIsoDate(nextFrom), to: toIsoDate(endOfMonth(addMonths(nextFrom, 11))) }
    }
    default: {
      const _exhaustive: never = unit
      return _exhaustive
    }
  }
}

/* -------------------------------------------------------------------------- */
/*                              Formatting                                    */
/* -------------------------------------------------------------------------- */

/** Format a duration in seconds as `Xh Ym` (omits the hour part if zero). */
export function formatDuration(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  if (hours === 0) return `${minutes}m`
  return `${hours}h ${pad2(minutes)}m`
}

/**
 * Format an ISO timestamp as `9:30 AM`. Forces `hour12: true` so en-GB
 * (and other 24-hour locales) render the same 12-hour shape we use for
 * the calendar grid's hour labels — keeps the day/week timesheet visually
 * consistent across locales.
 */
export function formatTime(iso: string, locale: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true })
}

/** `9:30 AM – 11:00 AM`, or `9:30 AM – running` for an open entry. */
export function formatTimeRange(startedAt: string, endedAt: string | null, locale: string): string {
  const start = formatTime(startedAt, locale)
  if (!endedAt) return `${start} – running`
  return `${start} – ${formatTime(endedAt, locale)}`
}

/**
 * Format a day-group header. Input: `YYYY-MM-DD`. Output: `Mon, May 11`.
 * Pinned to `en-US` so the order is always weekday-comma-month-day across
 * locales (en-GB would otherwise produce `Mon 11 May`, which doesn't
 * match the design). The `locale` arg is accepted for API symmetry with
 * other helpers but ignored for the format string.
 */
export function formatDayGroupHeader(isoDate: string, _locale: string): string {
  const d = parseIsoDate(isoDate)
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format a week-group header. Input: ISO-week key like `2026-W18`. Output:
 * `Week of Apr 27 – May 3`. If the input doesn't match the ISO-week
 * pattern, falls back to printing the raw key.
 */
export function formatWeekGroupHeader(weekKey: string, locale: string): string {
  const m = /^(\d{4})-W(\d{2})$/.exec(weekKey)
  if (!m) return weekKey
  const year = Number.parseInt(m[1] ?? '0', 10)
  const week = Number.parseInt(m[2] ?? '0', 10)
  const start = isoWeekToDate(year, week)
  const end = addDays(start, 6)
  const startStr = start.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
  const endStr = end.toLocaleDateString(locale, {
    month: start.getMonth() === end.getMonth() ? undefined : 'short',
    day: 'numeric',
  })
  return `Week of ${startStr} – ${endStr}`
}

/**
 * Convert an ISO-8601 (year, week) pair to the Monday `Date` of that week.
 * The ISO week year may differ from the calendar year in the first/last
 * few days of January/December — handled by the `+ ((week - 1) * 7)`.
 *
 * Exported because the reports chart kit parses bucket KEYS (`2026-W36`)
 * rather than the UTC instants beside them — the key is already in the
 * viewer's zone, the instant is not, so labelling from the instant
 * shifts a week label by a day for anyone east or west of UTC.
 */
export function isoWeekToDate(year: number, week: number): Date {
  // Jan 4th is always in ISO week 1.
  const jan4 = new Date(year, 0, 4, 12, 0, 0, 0)
  const jan4Day = (jan4.getDay() + 6) % 7 // 0 = Mon
  const week1Monday = addDays(jan4, -jan4Day)
  return addDays(week1Monday, (week - 1) * 7)
}

/* -------------------------------------------------------------------------- */
/*                              12-hour time inputs                           */
/* -------------------------------------------------------------------------- */

/**
 * AM/PM marker for the 12-hour time input compound used by the
 * TimeEntryModal duration selector (legacy parity).
 */
export type Period = 'AM' | 'PM'

/**
 * Shape-only sanitizer for the time text input. Strips non-digit, non-
 * colon characters; caps at 4 digits; auto-inserts a colon after the
 * second hour digit so typing `1130` lands as `11:30`. Deliberately does
 * NOT pad or clamp mid-keystroke — the previous implementation slammed
 * minutes to `00` the moment two hour digits were typed, which then
 * dropped any minute keystrokes the user tried to append. Final clamping
 * happens in `combineDateTimePeriod` at submit / blur time.
 */
export function validateTimeInput(value: string): string {
  if (!value) return ''
  const cleaned = value.replace(/[^\d:]/g, '')
  if (cleaned.includes(':')) {
    const idx = cleaned.indexOf(':')
    const h = cleaned.slice(0, idx).slice(0, 2)
    const m = cleaned
      .slice(idx + 1)
      .replace(/:/g, '')
      .slice(0, 2)
    return `${h}:${m}`
  }
  const digits = cleaned.slice(0, 4)
  if (digits.length <= 2) return digits
  return `${digits.slice(0, 2)}:${digits.slice(2)}`
}

/** Build a local Date from `YYYY-MM-DD`, 12-hour `H:MM`, and AM/PM. */
function combineDateTimePeriod(dateIso: string, time: string, period: Period): Date | null {
  const [hoursStr = '', minutesStr = '0'] = time.split(':')
  const hours12 = Number.parseInt(hoursStr, 10)
  const minutes = Number.parseInt(minutesStr, 10)
  if (!Number.isFinite(hours12) || !Number.isFinite(minutes)) return null
  let hours24 = hours12
  if (period === 'PM' && hours12 !== 12) hours24 += 12
  if (period === 'AM' && hours12 === 12) hours24 = 0
  const base = parseIsoDate(dateIso)
  if (!Number.isFinite(base.getTime())) return null
  base.setHours(hours24, minutes, 0, 0)
  return base
}

export interface DurationCalcResult {
  /** Display string, e.g. `"2h   :   30m"` or `"00:00"` if invalid/zero. */
  duration: string
  isValid: boolean
  isEndBeforeStart: boolean
  isMoreThanEightHours: boolean
  isZeroDuration: boolean
  totalMinutes: number
}

/**
 * Compute the duration between two 12-hour time + date pairs. Mirrors
 * legacy `calculateDuration` from `TimeEntry.utils.js`. Returns a flag
 * bundle so the modal can color the display red on invalid states
 * (end-before-start, >8h, zero-with-times).
 */
export function calculateDuration(
  startTime: string,
  startPeriod: Period | '',
  endTime: string,
  endPeriod: Period | '',
  startDate: string,
  endDate: string,
): DurationCalcResult {
  const empty: DurationCalcResult = {
    duration: '00:00',
    isValid: false,
    isEndBeforeStart: false,
    isMoreThanEightHours: false,
    isZeroDuration: true,
    totalMinutes: 0,
  }
  if (!startTime || !endTime || !startPeriod || !endPeriod || !startDate || !endDate) return empty

  const startDt = combineDateTimePeriod(startDate, startTime, startPeriod)
  const endDt = combineDateTimePeriod(endDate, endTime, endPeriod)
  if (!startDt || !endDt) return empty

  const diffMinutes = Math.floor((endDt.getTime() - startDt.getTime()) / 60_000)
  const sameDay = toIsoDate(startDt) === toIsoDate(endDt)
  const isEndBeforeStart = sameDay && diffMinutes <= 0
  if (isEndBeforeStart) {
    return {
      duration: '00:00',
      isValid: false,
      isEndBeforeStart: true,
      isMoreThanEightHours: false,
      isZeroDuration: true,
      totalMinutes: 0,
    }
  }

  const totalMinutes = Math.abs(diffMinutes)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const isMoreThanEightHours = hours > 8 || (hours === 8 && minutes > 0)
  const isZeroDuration = hours === 0 && minutes === 0
  const duration = isZeroDuration ? '00:00' : `${hours}h : ${minutes}m`

  return {
    duration,
    isValid: true,
    isEndBeforeStart: false,
    isMoreThanEightHours,
    isZeroDuration,
    totalMinutes,
  }
}

/** Split an ISO timestamp into local `{ time: 'H:MM', period: 'AM'|'PM', date: 'YYYY-MM-DD' }`. */
export function isoToTimeParts(iso: string): { time: string; period: Period; date: string } {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { time: '', period: 'AM', date: '' }
  const hours24 = d.getHours()
  const minutes = d.getMinutes()
  const period: Period = hours24 >= 12 ? 'PM' : 'AM'
  let hours12 = hours24 % 12
  if (hours12 === 0) hours12 = 12
  const time = `${pad2(hours12)}:${pad2(minutes)}`
  return { time, period, date: toIsoDate(d) }
}

/** Compose an ISO timestamp from local-time parts. Returns null on invalid input. */
export function timePartsToIso(time: string, period: Period, date: string): string | null {
  const d = combineDateTimePeriod(date, time, period)
  if (!d) return null
  return d.toISOString()
}
